import dayjs from 'dayjs'
import React from 'react'
import { Box, Typography } from '@mui/material'
import ReportTypeMarker from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/ReportTypeMarker'

/**
 * Timeline-style date marker for visit history. Visit + date on first row (left column),
 * time/type cards below in the same left column, report content on the right.
 * timeMarkers: array of { time, label } (one per report, same order as children).
 */
const TIMELINE_LEFT_WIDTH = 120
const TIMELINE_RIGHT_PADDING = 16
const HISTORY_PRESCRIPTION_A4_WIDTH = 794

export default function ConnectingLines({ date, visitNo, timeMarkers = [], children }) {
  const dateStr = date?.split(',')[0] || ''
  const currentDate = dayjs(dateStr, 'DD/MM/YYYY')
  const dayNum = currentDate.format('D')
  const monthShort = currentDate.format('MMM')
  const weekday = currentDate.format('ddd')
  const isValid = currentDate.isValid()

  const childArray = React.Children.toArray(children)

  return (
    <Box
      className="d-flex flex-column"
      sx={{
        position: 'relative',
        // Include timeline column + its right padding + A4 report width,
        // so the report pane can still render full 794px on wide screens.
        maxWidth: TIMELINE_LEFT_WIDTH + TIMELINE_RIGHT_PADDING + HISTORY_PRESCRIPTION_A4_WIDTH,
        width: '100%',
        mx: 'auto',
        py: 1,
        pb: 3,
      }}
    >
      {/* Colorful vertical line at horizontal center of date/time cards, behind the cards */}
      <Box
        className="hide-on-print-only"
        sx={{
          position: 'absolute',
          left: TIMELINE_LEFT_WIDTH / 2 - 1,
          top: 0,
          bottom: 0,
          width: 3,
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark ?? theme.palette.primary.main} 100%)`,
          borderRadius: 1,
          zIndex: 0,
        }}
      />
      {/* Row 1: Visit + Date (left), empty spacer (right) */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
        }}
      >
        <Box
          className="hide-on-print-only"
          sx={{
            width: TIMELINE_LEFT_WIDTH,
            flexShrink: 0,
            pr: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            component="span"
            variant="caption"
            sx={{
              display: 'inline-block',
              px: 1.25,
              py: 0.375,
              borderRadius: 10,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.02em',
            }}
          >
            Visit {visitNo}
          </Typography>
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              mt: 1,
              textAlign: 'center',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            {isValid ? (
              <>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.1,
                    color: 'text.primary',
                    pt: 1,
                    px: 1.25,
                  }}
                >
                  {dayNum}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    pb: 0.5,
                  }}
                >
                  {monthShort}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    pb: 0.75,
                  }}
                >
                  {weekday}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>
                {date}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1, minWidth: 0, minHeight: 60 }} />
      </Box>
      {/* Rows: time card (left) | report content (right) */}
      {childArray.map((child, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
          }}
        >
          <Box
            className="hide-on-print-only"
            sx={{
              width: TIMELINE_LEFT_WIDTH,
              flexShrink: 0,
              pr: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {timeMarkers[idx] && (
              <ReportTypeMarker
                inline
                time={timeMarkers[idx].time}
                label={timeMarkers[idx].label}
              />
            )}
          </Box>
          <Box sx={{ position: 'relative', zIndex: 1, flex: 1, minWidth: 0 }}>{child}</Box>
        </Box>
      ))}
    </Box>
  )
}
